/**
 * Central configuration for the Enterprise Search frontend.
 *
 * How values are resolved:
 * - During development / at build time: Vite inlines import.meta.env.VITE_* from .env files.
 * - In production containers: docker-entrypoint.sh writes /runtime-config.js from the
 *   VITE_* environment variables. index.html loads it before the app, and it sets
 *   `window.__RUNTIME_CONFIG__`.
 *
 * Runtime values deliberately live outside the content-hashed bundles: rewriting a
 * bundle at container start would change its content but not its file name, and
 * browsers that cached it as immutable would never see the change.
 *
 * To add a new configuration variable:
 * 1. Add a property using `env('VITE_X', import.meta.env.VITE_X)` (the second argument
 *    must stay a static expression so Vite can inline it)
 * 2. Add it to your .env.local for development
 * 3. Pass it as an environment variable when running the container
 */

const runtimeConfig: Record<string, string> = window.__RUNTIME_CONFIG__ ?? {};

/** Build-time value first, then the container's runtime value, else ''. */
function env(name: string, buildValue: string | undefined): string {
  return buildValue ?? runtimeConfig[name] ?? '';
}

function isTrue(value: string): boolean {
  return value.trim().toLowerCase() === 'true';
}

/**
 * Application configuration object.
 * All configuration values should be accessed through this object.
 */
export const config = {
  /**
   * OneLogin OIDC Configuration
   */
  oidc: {
    /** The OIDC authority/issuer URL (e.g., https://your-domain.onelogin.com/oidc/2) */
    authority: env('VITE_OIDC_AUTHORITY', import.meta.env.VITE_OIDC_AUTHORITY),
    
    /** The OIDC client ID for this application */
    clientId: env('VITE_OIDC_CLIENT_ID', import.meta.env.VITE_OIDC_CLIENT_ID),
    
    /** The redirect URI after successful login (defaults to current origin + /callback) */
    redirectUri: env('VITE_OIDC_REDIRECT_URI', import.meta.env.VITE_OIDC_REDIRECT_URI),
    
    /** The post-logout redirect URI */
    postLogoutRedirectUri: env(
      'VITE_OIDC_POST_LOGOUT_REDIRECT_URI',
      import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI
    ),
    
    /** The URL to the user profile page (e.g., SSO portal) */
    profileUrl: env('VITE_OIDC_PROFILE_URL', import.meta.env.VITE_OIDC_PROFILE_URL),
  },

  /**
   * API Configuration
   */
  api: {
    /** The base URL for the backend API */
    baseUrl: env('VITE_API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
  },

  /**
   * Feedback widget
   */
  feedback: {
    /** Support portal linked from the feedback dialog footer ("For other products…"). */
    portalUrl: env('VITE_FEEDBACK_PORTAL_URL', import.meta.env.VITE_FEEDBACK_PORTAL_URL),
  },

  /**
   * Feature Flags
   */
  features: {
    /** Enable debug mode (shows additional logging) */
    debug: isTrue(env('VITE_DEBUG', import.meta.env.VITE_DEBUG)),

    /** Enable Zoho PageSense analytics (should be enabled only in production) */
    analytics: isTrue(env('VITE_ANALYTICS_ENABLED', import.meta.env.VITE_ANALYTICS_ENABLED)),

    /** Enable Google Analytics (should be enabled only in production) */
    googleAnalytics: isTrue(env('VITE_GOOGLE_ANALYTICS_ENABLED', import.meta.env.VITE_GOOGLE_ANALYTICS_ENABLED)),
  },
} as const;

/**
 * Type for the config object
 */
export type AppConfig = typeof config;

/**
 * Return `undefined` for a value that was not configured.
 */
export function resolveRuntimeValue(value: string): string | undefined {
  return value || undefined;
}

/**
 * Validate that required configuration values are set.
 * Call this early in app initialization to catch missing config.
 */
export function validateConfig(): { valid: boolean; missing: string[] } {
  const requiredKeys = [
    { key: 'OIDC Authority', value: config.oidc.authority },
    { key: 'OIDC Client ID', value: config.oidc.clientId },
    { key: 'API Base URL', value: config.api.baseUrl },
  ];

  const missing = requiredKeys
    .filter(({ value }) => !value)
    .map(({ key }) => key);

  if (missing.length > 0 && config.features.debug) {
    console.warn('⚠️ Missing configuration values:', missing);
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

export default config;
